// T2T-05-C5 — Per-user notification preferences. 5 event categories ×
// 3 channels (in-app / email / WhatsApp). Locked-on cells (mention in-app,
// SLA breach in-app) are shown disabled to communicate "always on".

import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import T2TTopNav from "../Layout/T2TTopNav";
import { fetchPreferences, savePreferences } from "../../../api/t2tNotificationsApi";

const CATEGORIES = [
  { key: "mention",         label: "@Mentions",          desc: "Someone mentions you in a chat thread" },
  { key: "vendor_reply",    label: "Vendor replies",     desc: "An external vendor posts on a request you're on" },
  { key: "request_updates", label: "Request updates",    desc: "New chat messages, stage changes, system updates" },
  { key: "sla_breach",      label: "SLA alerts",         desc: "When a request you own is at risk or has breached its SLA" },
  { key: "assigned",        label: "Assigned to you",    desc: "When a request is routed to you for action" },
];

const CHANNELS = [
  { key: "in_app",   label: "In-app",   icon: "bi-bell-fill" },
  { key: "email",    label: "Email",    icon: "bi-envelope" },
  { key: "whatsapp", label: "WhatsApp", icon: "bi-whatsapp" },
];

// These cells are locked ON regardless of preference.
const LOCKED_ON = new Set(["mention.in_app", "sla_breach.in_app"]);

export default function NotificationPreferencesPage() {
  const navigate = useNavigate();
  const [prefs, setPrefs]   = useState(null);
  const [saving, setSaving] = useState(false);
  const [savedAt, setSavedAt] = useState(null);
  const [error, setError]   = useState(null);

  useEffect(() => {
    (async () => {
      const resp = await fetchPreferences();
      if (resp.error) {
        setError("Could not load preferences.");
        return;
      }
      setPrefs(resp.data?.data || {});
    })();
  }, []);

  const isOn = (cat, ch) => {
    if (LOCKED_ON.has(`${cat}.${ch}`)) return true;
    if (!prefs) return false;
    return prefs[`${cat}_${ch}`] === 1;
  };

  const toggle = async (cat, ch) => {
    const key = `${cat}.${ch}`;
    if (LOCKED_ON.has(key)) return;
    const colName = `${cat}_${ch}`;
    const next = !isOn(cat, ch);
    const prevSnapshot = { ...prefs };
    // Optimistic
    setPrefs((p) => ({ ...p, [colName]: next ? 1 : 0 }));
    setSaving(true);
    setError(null);
    const resp = await savePreferences({ [colName]: next ? 1 : 0 });
    setSaving(false);
    if (resp.error) {
      setError("Could not save preference. Reverted.");
      setPrefs(prevSnapshot);
    } else {
      setSavedAt(new Date());
    }
  };

  return (
    <div className="t2t-module" style={{ minHeight: "100vh", background: "var(--t2t-bg)" }}>
      <T2TTopNav title="Notification preferences" actions={
        <button className="t2t-btn t2t-btn-ghost t2t-btn-sm" onClick={() => navigate("/t2t/notifications")}>
          <i className="bi bi-arrow-left" /> Back to notifications
        </button>
      } />

      <div style={{ maxWidth: 820, margin: "20px auto", padding: "0 18px" }}>
        {error && (
          <div role="alert" style={{
            background: "#FEE2E2", color: "#DC2626", border: "1px solid #FECACA",
            borderRadius: "var(--t2t-radius)", padding: "8px 12px", fontSize: 12,
            marginBottom: 14,
          }}>
            <i className="bi bi-exclamation-triangle-fill" /> {error}
          </div>
        )}

        <div style={{ background: "var(--t2t-surface)", border: "1px solid var(--t2t-border)", borderRadius: "var(--t2t-radius-lg)", overflow: "hidden" }}>
          <div style={{ padding: "14px 18px", borderBottom: "1px solid var(--t2t-border)", display: "flex", alignItems: "center" }}>
            <div style={{ flex: 1 }}>
              <p style={{ fontWeight: 700, fontSize: 14, color: "var(--t2t-text-primary)", margin: 0 }}>
                Choose how you want to be notified
              </p>
              <p style={{ fontSize: 11, color: "var(--t2t-text-muted)", margin: "4px 0 0" }}>
                In-app for @mentions and SLA alerts is always on for safety.
              </p>
            </div>
            {saving && <span style={{ fontSize: 11, color: "var(--t2t-text-muted)" }}>Saving…</span>}
            {!saving && savedAt && (
              <span style={{ fontSize: 11, color: "var(--t2t-success)" }}>
                <i className="bi bi-check2" /> Saved
              </span>
            )}
          </div>

          {!prefs ? (
            <div style={{ padding: 24 }}>
              {[80, 70, 90, 60, 75].map((w, i) => (
                <div key={i} className="kb-skeleton-bar" style={{ width: `${w}%`, height: 14, marginBottom: 14, borderRadius: 6 }} />
              ))}
            </div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12 }}>
              <thead>
                <tr style={{ background: "var(--t2t-bg)", borderBottom: "1px solid var(--t2t-border)" }}>
                  <th style={{ textAlign: "left", padding: "10px 18px", fontWeight: 600, color: "var(--t2t-text-secondary)" }}>Event</th>
                  {CHANNELS.map(c => (
                    <th key={c.key} style={{ textAlign: "center", padding: "10px 14px", fontWeight: 600, color: "var(--t2t-text-secondary)", width: 100 }}>
                      <i className={`bi ${c.icon}`} /> {c.label}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {CATEGORIES.map((cat, i) => (
                  <tr key={cat.key} style={{ borderBottom: i < CATEGORIES.length - 1 ? "1px solid var(--t2t-border)" : "none" }}>
                    <td style={{ padding: "12px 18px" }}>
                      <div style={{ fontWeight: 600, color: "var(--t2t-text-primary)" }}>{cat.label}</div>
                      <div style={{ fontSize: 11, color: "var(--t2t-text-muted)", marginTop: 2 }}>{cat.desc}</div>
                    </td>
                    {CHANNELS.map(ch => {
                      const locked = LOCKED_ON.has(`${cat.key}.${ch.key}`);
                      const on = isOn(cat.key, ch.key);
                      return (
                        <td key={ch.key} style={{ textAlign: "center", padding: "12px 14px" }}>
                          <button
                            type="button"
                            onClick={() => toggle(cat.key, ch.key)}
                            disabled={locked}
                            title={locked ? "Always on" : (on ? "Click to mute" : "Click to enable")}
                            style={{
                              width: 38, height: 22, borderRadius: 11,
                              border: "1px solid var(--t2t-border)",
                              background: on ? "var(--t2t-primary)" : "var(--t2t-bg)",
                              position: "relative",
                              cursor: locked ? "not-allowed" : "pointer",
                              opacity: locked ? 0.6 : 1,
                              transition: "background var(--t2t-transition)",
                            }}
                          >
                            <span style={{
                              position: "absolute",
                              top: 2,
                              left: on ? 18 : 2,
                              width: 16, height: 16, borderRadius: "50%",
                              background: "#fff",
                              boxShadow: "0 1px 3px rgba(0,0,0,0.2)",
                              transition: "left var(--t2t-transition)",
                            }} />
                          </button>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
    </div>
  );
}
